import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextValue>({ socket: null, isConnected: false });

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { currentCompany } = useCompany();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      return;
    }

    // Auth is carried by the HttpOnly session cookie — withCredentials lets
    // Socket.IO include it on the upgrade handshake.
    const socket = io('/', {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [isAuthenticated]);

  // Join company room when current company changes
  useEffect(() => {
    // Server rooms are keyed by the company's real id (what req.companyId
    // carries); `_id` is a leftover from the Supabase days and is undefined
    // on today's API objects — joining `company:undefined` meant no tab ever
    // received a live update.
    const companyId = (currentCompany as any)?.id ?? currentCompany?._id ?? currentCompany?.uuid;
    if (socketRef.current?.connected && companyId) {
      socketRef.current.emit('join:company', companyId);
    }
  }, [currentCompany, isConnected]);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
